import { useAuth0 } from "@auth0/auth0-react"
import { useCryptr } from "@killiangabrielkamae/cryptr-react"
import { useCallback, useEffect, useState } from "react"
import { useAuth } from "react-oidc-context"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { shouldThePathBeMemoized } from "../utils"
import { useAdministrationPath } from "./useAdministrationPath"

const UNAUTHENTICATED_ALLOWED_PATHS = [
  "/unauthorized",
  "/forbidden",
  "/company-not-found",
  "/user-not-found",
  "/bot",
  "/learning-page",
]

// Test seam: in e2e runs (VITE_REACT_APP_E2E=true at serve/build time) we bypass
// the real auth providers and start authenticated with a mock token, so Playwright
// can exercise the app without a real identity provider. Never enabled in prod builds.
const IS_E2E = import.meta.env.VITE_REACT_APP_E2E === "true"
const E2E_MOCK_JWT = "e2e-mock-jwt-token"

// Detects the active auth provider (Cryptr / Auth0 / AXA OIDC), stores the token
// and returns it. Redirects unauthenticated users to /login (or the Vanta flow).
export function useAuthentication(): string | null {
  const [jwt, setJwt] = useState<string | null>(IS_E2E ? E2E_MOCK_JWT : null)

  const cryptr = useCryptr()
  const auth0 = useAuth0()
  const oidcAxa = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const getAdministrationPath = useAdministrationPath()

  const requestAccessTokenCryptr = useCallback(() => {
    const token = cryptr.getCurrentAccessToken()
    setJwt(token ?? "")
    localStorage.setItem("jwt", token ?? "")
  }, [cryptr])

  const requestAccessTokenAuth0 = useCallback(async () => {
    const token = await auth0.getAccessTokenSilently()
    setJwt(token)
    localStorage.setItem("jwt", token)
  }, [auth0])

  const requestIdTokenOidcAxa = useCallback(() => {
    const idToken = oidcAxa.user.id_token
    setJwt(idToken)
    localStorage.setItem("jwt", idToken)
  }, [oidcAxa])

  useEffect(() => {
    // In e2e mode we are already authenticated with a mock token: never touch the
    // real providers nor redirect to /login.
    if (IS_E2E) return
    if (
      !cryptr.isAuthenticated() &&
      !cryptr.isLoading &&
      !auth0.isLoading &&
      !auth0.isAuthenticated &&
      !oidcAxa.isLoading &&
      !oidcAxa.isAuthenticated &&
      !UNAUTHENTICATED_ALLOWED_PATHS.includes(location.pathname)
    ) {
      const state = searchParams.get("state")
      const code = searchParams.get("code")
      if (state === null) {
        // Memoized current url for redirection after login
        if (shouldThePathBeMemoized(location.pathname)) {
          localStorage.setItem("redirectTo", location.pathname)
        }
        navigate(`/login${document.location.search}`)
      } else {
        window.location.replace(`${getAdministrationPath()}?vantaState=${state}&code=${code}`)
      }
    } else if (cryptr.isAuthenticated()) {
      requestAccessTokenCryptr()
    } else if (auth0.isAuthenticated) {
      requestAccessTokenAuth0()
    } else if (oidcAxa.isAuthenticated) {
      requestIdTokenOidcAxa()
    }
  }, [
    cryptr,
    auth0,
    oidcAxa,
    navigate,
    location.pathname,
    searchParams,
    getAdministrationPath,
    requestAccessTokenCryptr,
    requestAccessTokenAuth0,
    requestIdTokenOidcAxa,
  ])

  return jwt
}
